/**
 * Participants Store
 *
 * Caches full participant details (phone, email, etc.) in IndexedDB
 * so ParticipantDetailView works offline.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { participantsApi } from '@/api/participants.api'
import { db } from '@/db'
import type { ParticipantDto } from '@/types/api.types'

export const useParticipantsStore = defineStore('participants', () => {
  // State
  const participants = ref<Map<number, ParticipantDto>>(new Map())
  const isLoading = ref(false)
  const lastFetched = ref<number>(0)

  // Cache duration: 10 minutes
  const CACHE_DURATION = 10 * 60 * 1000

  // Getters
  const getById = computed(() => {
    return (id: number): ParticipantDto | undefined => participants.value.get(id)
  })

  // Actions

  /**
   * Fetch all participants and cache in IndexedDB.
   * Skips API call if cache is fresh or offline with data.
   */
  async function fetchAll(forceRefresh = false): Promise<void> {
    const isCacheValid = Date.now() - lastFetched.value < CACHE_DURATION
    if (isCacheValid && !forceRefresh && participants.value.size > 0) return

    // Offline + data in memory → skip
    if (!navigator.onLine && participants.value.size > 0) return

    isLoading.value = true
    try {
      const data = await participantsApi.getAll()

      // Update in-memory map
      const map = new Map<number, ParticipantDto>()
      for (const p of data) map.set(p.id, p)
      participants.value = map
      lastFetched.value = Date.now()

      // Cache in IndexedDB
      await db.transaction('rw', db.participants, async () => {
        await db.participants.clear()
        await db.participants.bulkPut(data)
      })
    } catch {
      // On failure, load from IndexedDB if memory is empty
      if (participants.value.size === 0) {
        const cached = await db.participants.toArray()
        if (cached.length > 0) {
          const map = new Map<number, ParticipantDto>()
          for (const p of cached) map.set(p.id, p)
          participants.value = map
        }
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get a single participant, from memory → IndexedDB → API (with cache-write).
   */
  async function fetchById(id: number): Promise<ParticipantDto | null> {
    // 1. Memory
    const inMemory = participants.value.get(id)
    if (inMemory) return inMemory

    // 2. IndexedDB
    const cached = await db.participants.get(id)
    if (cached) {
      participants.value.set(id, cached)
      return cached
    }

    // 3. API (online only — interceptor blocks offline)
    try {
      const data = await participantsApi.getById(id)
      participants.value.set(id, data)
      await db.participants.put(data)
      return data
    } catch {
      return null
    }
  }

  function $reset(): void {
    participants.value.clear()
    lastFetched.value = 0
    isLoading.value = false
  }

  return {
    participants,
    isLoading,
    getById,
    fetchAll,
    fetchById,
    $reset,
  }
})
